import React, {
	forwardRef,
	useEffect,
	useMemo,
	useRef,
	useState,
	type PropsWithChildren,
} from 'react';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { defineMessage, useIntl } from '@jira/platform__react-intl';
import { START_NODE_ID } from '@jira/project-settings__workflow-types/src/workflowConstants.tsx';
import {
	applyTransitionIconConditions,
	getStartNodeLayer,
	getStatusLayer,
	getTransitionLayer,
	getTransitionSource,
} from './calculateDefaultGlobalLoopsContainerPosition.tsx';
import { CanvasSvg } from './CanvasSvg.tsx';
import Port from './DiagramPort.tsx';
import Status from './DiagramStatus.tsx';
import { DIAGRAM_CONTAINER_TYPE } from './directionKeys.tsx';
import DirectTransition from './DirectTransition.tsx';
import { DEFAULT_PAN, DEFAULT_ZOOM_LEVEL, MINIMUM_ZOOM_LEVEL } from './ellipsis.tsx';
import GlobalLoopsContainer from './GlobalLoopsContainer.tsx';
import GlobalTransition from './GlobalTransition.tsx';
import GlobalTransitionMulti from './GlobalTransitionMulti.tsx';
import { useTransitionRerouting } from './isInvalidConnectionPort.tsx';
import { LayerGroup, LayerItem } from './LayerItem.tsx';
import type {
	Positionable,
	ItemReference,
	ViewBoxRect,
	ExpandableItemReference,
} from './ListEdgeDetails.tsx';
import ProposedTransition from './ProposedTransition.tsx';
import SharedTransition from './SharedTransition.tsx';
import SnapLines from './SnapLines.tsx';
import StartNode from './StartNode.tsx';
import {
	type DiagramStatus,
	type DiagramTransition,
	type Point,
	LayerIndex,
	TransitionIcons,
	type StatefulDiagramDirectTransition,
	type StatefulDiagramSharedTransition,
	type StatefulDiagramGlobalLoopedTransition,
	type StatefulDiagramGlobalTransition,
} from './toPortInfo.tsx';
import { usePorts } from './toSquareUnitDirection.tsx';
import TransitionLabel from './TransitionLabel.tsx';
import { useAutoFitZoom } from './useAutoFitZoom.tsx';
import { useAutoLayout } from './useAutoLayout.tsx';
import { useCanvasDragDropEvents } from './useCanvasDragDropEvents.tsx';
import { useCanvasView } from './useCanvasView.tsx';
import { useControllable } from './useControllable.tsx';
import { useGlobalLoopsContainer } from './useGlobalLoopsContainer.tsx';
import { useItemDragDrop } from './useItemDragDrop.tsx';
import { useLatest } from './useLatest.tsx';
import { useOnWheel } from './useOnWheel.tsx';
import { usePortDragAndDrop } from './usePortDragAndDrop.tsx';
import { useRecordState } from './useRecordState.tsx';
import { useSnapLines } from './useSnapLines.tsx';
import { useStartNode } from './useStartNode.tsx';
import { useStatefulStatuses } from './useStatefulStatuses.tsx';
import { useStatefulTransitions } from './useStatefulTransitions.tsx';
import { useTransitionDragDrop } from './useTransitionDragDrop.tsx';
import { useTransitionProposals } from './useTransitionProposals.tsx';
import ViewOverlay from './ViewOverlay.tsx';

type Props = {
	isImmutable?: boolean;
	zoom?: number;
	pan?: Point;
	selectedItem?: ItemReference;
	targetedItem?: ItemReference;
	expandedItem?: ExpandableItemReference;
	movingItem?: Positionable;
	startNodePosition: Point | undefined;
	globalLoopsContainerPosition?: Point;
	transitions: DiagramTransition[];
	statuses: DiagramStatus[];
	collapseTransitionLabels?: boolean;
	shouldShowGlobalLoops?: boolean;
	minimumZoom?: number;
	onClearGlobalLoopsContainerPosition: () => void;
	onRenderComplete?: () => void;
	onSelect?: (item: ItemReference | undefined) => void;
	onExpandedChange?: (item: ExpandableItemReference | undefined) => void;
	onItemMove?: (payload: Positionable) => void;
	onTransitionCreate?: (transition: DiagramTransition) => void;
	onTransitionPortChange?: (transition: DiagramTransition) => void;
	onViewBoxChange?: (viewBox: ViewBoxRect) => void;
	onZoomChange?: (zoom: number) => void;
	onPanChange?: (pan: Point) => void;
	onMinZoomChange?: (minZoom: number) => void;
};

const useStatusWidthsWithReadinessCheck = (statuses: DiagramStatus[]) => {
	const [statusWidths, setStatusWidths, setStatusWidthEntry] = useRecordState<string, number>();

	return useMemo(() => {
		const isReady = statuses.length === Object.keys(statusWidths).length;
		return [statusWidths, setStatusWidths, setStatusWidthEntry, isReady] as const;
	}, [setStatusWidthEntry, setStatusWidths, statusWidths, statuses.length]);
};

const WorkflowDiagram = ({
	isImmutable = false,
	pan: controlledPan,
	zoom: controlledZoom,
	startNodePosition,
	globalLoopsContainerPosition: providedGlobalLoopsContainerPosition,
	onClearGlobalLoopsContainerPosition,
	transitions,
	statuses,
	selectedItem,
	targetedItem,
	expandedItem,
	movingItem,
	onExpandedChange,
	onSelect,
	onItemMove,
	onTransitionCreate,
	onTransitionPortChange,
	onZoomChange,
	onPanChange,
	onViewBoxChange,
	onRenderComplete,
	onMinZoomChange,
	collapseTransitionLabels = false,
	shouldShowGlobalLoops = false,
	minimumZoom = MINIMUM_ZOOM_LEVEL,
}: Props): React.JSX.Element => {
	const rootSvgRef = useRef(null);
	const [hoveredItem, setHoveredItem] = useState<ItemReference>();
	const { formatMessage } = useIntl();

	const [statusWidths, , setStatusWidthEntry, isStatusWidthsReady] =
		useStatusWidthsWithReadinessCheck(statuses);

	const [pan, updatePan] = useControllable(controlledPan, onPanChange, DEFAULT_PAN);

	const [zoom, updateZoom] = useControllable(controlledZoom, onZoomChange, DEFAULT_ZOOM_LEVEL);

	const { canvasContainerRef, diagramSize, viewBox } = useCanvasView(zoom, pan, onViewBoxChange);

	const [
		reroutingTransition,
		{ onTransitionReroutingCancel, onTransitionReroutingClose, onTransitionReroutingMove },
	] = useTransitionRerouting();

	const [
		proposedTransition,
		{ onTransitionProposalCancel, onTransitionProposalMove, onTransitionProposalClose },
	] = useTransitionProposals();

	const globalLoopedTransitions = useMemo(
		() => transitions.filter((t) => t.type === 'global-looped'),
		[transitions],
	);

	const {
		isLayoutStable,
		defaultPositions,
		defaultGlobalLoopsContainerPosition,
		transitionsWithDefaultPorts,
		contentBounds,
	} = useAutoLayout({
		rootSvgRef,
		canvasContainerRef,
		startNodePosition,
		transitions,
		statuses,
		statusWidths,
		isStatusWidthsReady,
		providedGlobalLoopsContainerPosition,
		onClearGlobalLoopsContainerPosition,
		onItemMove,
		onTransitionPortChange,
		onPan: updatePan,
		onZoom: updateZoom,
		globalLoopedTransitions,
	});

	const latestOnRenderComplete = useLatest(onRenderComplete);
	useEffect(() => {
		if (isLayoutStable) {
			latestOnRenderComplete.current?.();
		}
	}, [isLayoutStable, latestOnRenderComplete]);

	const dynamicMinZoom = useAutoFitZoom(
		contentBounds,
		canvasContainerRef,
		isLayoutStable,
		minimumZoom,
		expVal('hix-8229-dynamic-zoom-experiment', 'isEnabled', false),
	);

	const effectiveMinZoom = expVal('hix-8229-dynamic-zoom-experiment', 'isEnabled', false)
		? dynamicMinZoom
		: minimumZoom;
	const effectiveContentBounds = expVal('hix-8229-dynamic-zoom-experiment', 'isEnabled', false)
		? contentBounds
		: undefined;

	const { dragDropState, temporaryPan } = useCanvasDragDropEvents({
		rootSvgRef,
		zoom,
		pan,
		viewBox,
		isImmutable,
		updatePan,
		onSelect,
		contentBounds: effectiveContentBounds,
	});

	const latestOnMinZoomChange = useLatest(onMinZoomChange);
	useEffect(() => {
		latestOnMinZoomChange.current?.(effectiveMinZoom);
	}, [effectiveMinZoom, latestOnMinZoomChange]);

	const { snapLines, snappedSubjectPosition } = useSnapLines({
		isImmutable,
		startNodePosition,
		globalLoopsContainerPosition: providedGlobalLoopsContainerPosition,
		statuses,
		transitions,
		statusWidths,
		movingItem,
		dragDropState,
	});

	const { draggingItem } = useItemDragDrop({
		isImmutable,
		dragDropState,
		snappedSubjectPosition,
		onItemMove,
	});

	const globalLoopsContainer = useGlobalLoopsContainer({
		providedGlobalLoopsContainerPosition,
		defaultGlobalLoopsContainerPosition,
		targetedItem,
		draggingItem,
		movingItem,
	});

	const startNode = useStartNode({
		startNodePosition,
		targetedItem,
		hoveredItem,
		movingItem,
		draggingItem,
		defaultPositions,
	});

	const statefulStatuses = useStatefulStatuses({
		statuses,
		selectedItem,
		targetedItem,
		hoveredItem,
		draggingItem,
		movingItem,
		defaultPositions,
	});

	const { ports, visiblePorts } = usePorts({
		hoveredItem,
		selectedItem,
		startNodePosition: startNode.position,
		transitions,
		statuses: statefulStatuses,
		statusWidths,
		defaultPositions,
		dragDropState,
		reroutingTransition,
		proposedTransition,
		isImmutable,
	});

	const statefulTransitions = useStatefulTransitions({
		transitions,
		selectedItem,
		targetedItem,
		hoveredItem,
		ports,
		reroutingTransition,
		transitionsWithDefaultPorts,
	});

	const statefulGlobalTransitionsPerStatus = useMemo(() => {
		const globals = statefulTransitions.filter((transition) => transition.type === 'global');
		const globalsPerStatus: Record<string, StatefulDiagramGlobalTransition[]> = globals.reduce(
			(acc, transition) => {
				const statusId = transition.targetPort?.statusId;
				if (statusId) {
					acc[statusId] = acc[statusId] || [];
					acc[statusId].push(transition);
				}
				return acc;
			},
			// oxlint-disable-next-line typescript/consistent-type-assertions
			{} as Record<string, StatefulDiagramGlobalTransition[]>,
		);
		return globalsPerStatus;
	}, [statefulTransitions]);

	const statefulDirectTransitions = useMemo(() => {
		return statefulTransitions.filter((transition) => transition.type === 'direct');
	}, [statefulTransitions]);

	const statefulSharedTransitions = useMemo(() => {
		return statefulTransitions.filter((transition) => transition.type === 'shared');
	}, [statefulTransitions]);

	const onWheel = useOnWheel({
		pan,
		zoom,
		updateZoom,
		updatePan,
		minZoom: effectiveMinZoom,
		contentBounds: effectiveContentBounds,
		viewBox,
	});

	usePortDragAndDrop({
		ports,
		isImmutable,
		selectedItem,
		transitions,
		dragDropState,
		onTransitionReroutingMove,
		onTransitionProposalMove,
	});

	useTransitionDragDrop({
		ports,
		dragDropState,
		proposedTransition,
		reroutingTransition,
		transitions,
		onTransitionProposalCancel,
		onTransitionReroutingCancel,
		onTransitionCreate,
		onTransitionPortChange,
	});

	useEffect(() => {
		const containerRef = canvasContainerRef?.current;

		if (containerRef) {
			// @ts-expect-error - WheelEvent is the correct value
			containerRef.addEventListener('wheel', onWheel, { passive: false });
		}

		return () => {
			if (containerRef) {
				// @ts-expect-error - WheelEvent is the correct value
				containerRef.removeEventListener('wheel', onWheel);
			}
		};
	}, [canvasContainerRef, onWheel]);

	return (
		<DiagramContainer
			ref={canvasContainerRef}
			isContentHidden={!isLayoutStable}
			isTargeted={targetedItem?.type === DIAGRAM_CONTAINER_TYPE}
		>
			<CanvasSvg
				rootSvgRef={rootSvgRef}
				pan={temporaryPan || pan}
				zoom={zoom}
				diagramSize={diagramSize}
			>
				<LayerGroup>
					{shouldShowGlobalLoops && globalLoopsContainer.position && (
						<LayerItem layer={LayerIndex.GlobalLoopsContainer}>
							<GlobalLoopsContainer
								isImmutable={isImmutable}
								position={globalLoopsContainer.position}
								transitions={statefulTransitions.filter(
									(x): x is StatefulDiagramGlobalLoopedTransition => x.type === 'global-looped',
								)}
								onSelect={onSelect}
								onHover={setHoveredItem}
								isMoving={globalLoopsContainer.isMoving}
								isTargeted={globalLoopsContainer.isTargeted}
							/>
						</LayerItem>
					)}

					{/* Transition paths */}
					{/* Global transitions grouped by status */}
					{Object.entries(statefulGlobalTransitionsPerStatus).map(([statusId, globalTransitions]) =>
						globalTransitions.length === 1 ? (
							<LayerItem
								key={`transitionPath_${globalTransitions[0].id}`}
								layer={getTransitionLayer(globalTransitions[0])}
							>
								<GlobalTransition
									id={globalTransitions[0].id}
									flag={globalTransitions[0].flag}
									isHovered={globalTransitions[0].isHovered}
									isImmutable={isImmutable}
									isSelected={globalTransitions[0].isSelected}
									isTargeted={globalTransitions[0].isTargeted}
									isHighlighted={globalTransitions[0].isHighlighted}
									portPosition={globalTransitions[0].targetPort?.position}
									onSelect={onSelect}
									onHover={setHoveredItem}
									icons={applyTransitionIconConditions(globalTransitions[0].icons, undefined)}
								/>
							</LayerItem>
						) : (
							<LayerItem key={`transitionPath_${statusId}`} layer={LayerIndex.TransitionsDefault}>
								<GlobalTransitionMulti
									statusId={statusId}
									portPosition={globalTransitions[0].targetPort?.position}
									transitions={globalTransitions}
									expandedItem={expandedItem}
									targetedItem={targetedItem}
									hoveredItem={hoveredItem}
									isImmutable={isImmutable}
									onExpandedChange={onExpandedChange}
									onSelect={onSelect}
									onHover={setHoveredItem}
								/>
							</LayerItem>
						),
					)}
					{/* Direct transitions */}
					{statefulDirectTransitions.map((transition) => (
						<LayerItem
							key={`transitionPath_${transition.id}`}
							layer={getTransitionLayer(transition)}
						>
							<DirectTransition
								id={transition.id}
								path={transition.path}
								flag={transition.flag}
								isImmutable={isImmutable}
								isSelected={transition.isSelected}
								isTargeted={transition.isTargeted}
								isHighlighted={transition.isHighlighted}
								isHovered={transition.isHovered}
								start={transition.sourcePort}
								end={transition.targetPort}
								onSelect={onSelect}
								onHover={setHoveredItem}
							/>
						</LayerItem>
					))}
					{/* Shared transitions */}
					{statefulSharedTransitions.map((transition) => (
						<LayerItem
							key={`transitionPath_${transition.id}`}
							layer={getTransitionLayer(transition)}
						>
							<SharedTransition
								id={transition.id}
								flag={transition.flag}
								isHovered={transition.isHovered}
								isImmutable={isImmutable}
								isSelected={transition.isSelected}
								isTargeted={transition.isTargeted}
								isHighlighted={transition.isHighlighted}
								connections={transition.connections}
								onSelect={onSelect}
								onHover={setHoveredItem}
							/>
						</LayerItem>
					))}

					{/* Transition labels */}
					{statefulTransitions
						.filter(
							(
								transition,
							): transition is
								| StatefulDiagramDirectTransition
								| StatefulDiagramSharedTransition => {
								if (transition.type === 'global') {
									return false;
								}

								if (transition.type === 'global-looped') {
									return false;
								}

								return true;
							},
						)
						.map((transition) => (
							<LayerItem
								key={`transitionLabel_${transition.id}`}
								layer={getTransitionLayer(transition)}
							>
								{transition.type === 'shared' ? (
									<>
										{transition.connections?.map((connection, i) => (
											<TransitionLabel
												id={transition.id}
												key={i}
												label={transition.label}
												icons={applyTransitionIconConditions(
													transition.icons,
													connection.sourcePort.statusId,
												).concat(TransitionIcons.SharedTransition)}
												flag={transition.flag}
												position={connection.labelPosition}
												isImmutable={isImmutable}
												isHovered={transition.isHovered}
												isSelected={transition.isSelected}
												isTargeted={transition.isTargeted}
												isHighlighted={transition.isHighlighted}
												isOverlapping={transition.isOverlapping}
												isOverlapHovered={transition.isOverlapHovered}
												onSelect={onSelect}
												onHover={setHoveredItem}
												collapseTransitionLabels={collapseTransitionLabels}
												connection={connection}
												hoveredItem={hoveredItem}
											/>
										))}
									</>
								) : (
									<TransitionLabel
										id={transition.id}
										label={transition.label}
										icons={applyTransitionIconConditions(
											transition.icons,
											getTransitionSource(transition),
										)}
										flag={transition.flag}
										position={transition.labelPosition}
										isImmutable={isImmutable}
										isHovered={transition.isHovered}
										isSelected={transition.isSelected}
										isTargeted={transition.isTargeted}
										isHighlighted={transition.isHighlighted}
										isOverlapping={transition.isOverlapping}
										isOverlapHovered={transition.isOverlapHovered}
										onSelect={onSelect}
										onHover={setHoveredItem}
										collapseTransitionLabels={collapseTransitionLabels}
										connection={{
											sourcePort: transition.sourcePort,
											targetPort: transition.targetPort,
										}}
										hoveredItem={hoveredItem}
									/>
								)}
							</LayerItem>
						))}

					{statefulStatuses.map((status) => (
						<LayerItem key={`status_${status.id}`} layer={getStatusLayer(status)}>
							<Status
								id={status.id}
								label={status.label || formatMessage(unknownStatusLabel)}
								position={status.position}
								category={status.category}
								flag={status.flag}
								isImmutable={isImmutable}
								isSelected={status.isSelected}
								isTargeted={status.isTargeted}
								isMoving={status.isMoving}
								showGlobalScopeIndicator={status.showGlobalScopeIndicator}
								onHover={setHoveredItem}
								onStatusResize={setStatusWidthEntry}
								onSelect={onSelect}
							/>
						</LayerItem>
					))}

					<LayerItem key={START_NODE_ID} layer={getStartNodeLayer(startNode)}>
						<StartNode
							id={START_NODE_ID}
							position={startNode.position}
							isImmutable={isImmutable}
							isTargeted={startNode.isTargeted}
							isMoving={startNode.isMoving}
							onHover={setHoveredItem}
						/>
					</LayerItem>

					<LayerItem layer={LayerIndex.Proposals}>
						<SnapLines lines={snapLines} />
					</LayerItem>

					{visiblePorts.map((port) => (
						<LayerItem key={`port_${port.key}`} layer={LayerIndex.Ports}>
							<Port
								portKey={port.key}
								isImmutable={isImmutable}
								position={port.position}
								isGlowing={port.isGlowing}
								parentStatusId={port.statusId}
								portIndex={port.portIndex}
								onHover={setHoveredItem}
								isProposingTransition={proposedTransition.stage !== 'none'}
								isRerouting={reroutingTransition.stage !== 'none'}
								onTransitionReroutingClose={onTransitionReroutingClose}
								onTransitionProposalClose={onTransitionProposalClose}
							/>
						</LayerItem>
					))}

					{proposedTransition.stage !== 'none' && (
						<LayerItem layer={LayerIndex.Proposals}>
							<ProposedTransition
								start={proposedTransition.line.start}
								end={proposedTransition.line.end}
							/>
							<Port isImmutable position={proposedTransition.line.end} />
						</LayerItem>
					)}
					{(movingItem || draggingItem) && (
						<LayerItem layer={LayerIndex.PrioritisedTransitions}>
							<ViewOverlay viewBox={viewBox} />
						</LayerItem>
					)}
				</LayerGroup>
			</CanvasSvg>
		</DiagramContainer>
	);
};

export default WorkflowDiagram;

type ContainerProps = PropsWithChildren<{ isContentHidden: boolean; isTargeted: boolean }>;

const DiagramContainer = forwardRef<HTMLDivElement, ContainerProps>(
	({ isContentHidden, isTargeted, children }, ref) => (
		<Box
			ref={ref}
			xcss={[
				containerBaseStyles,
				isContentHidden && containerHiddenStyles,
				isTargeted && containerTargetedStyles,
			]}
		>
			{children}
		</Box>
	),
);

const containerBaseStyles = xcss({
	height: '100%',
	minHeight: '336px',
	width: '100%',
	// disables browser handling of all touch events, giving us full control
	touchAction: 'none',
});

const containerHiddenStyles = xcss({
	opacity: '0',
});

const containerTargetedStyles = xcss({
	outline: `${token('border.width.focused')} solid`,
	outlineColor: 'color.border.focused',
});

// eslint-disable-next-line jira/i18n/no-export-define-message
export const unknownStatusLabel: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'accessible-workflow-diagram.workflow-diagram.unknown-status-label',
	defaultMessage: 'Unknown',
	description: 'Label for a unknown status in the workflow diagram',
});
